import { useState, useRef, useCallback, useEffect } from 'react';

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  // Event handlers (preferred for Safari compatibility)
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => void) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => void) | null;
  onend: ((this: SpeechRecognition, ev: Event) => void) | null;
  onaudiostart: ((this: SpeechRecognition, ev: Event) => void) | null;
  onaudioend: ((this: SpeechRecognition, ev: Event) => void) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => void) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

// Utility to detect Safari browser
export const isSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('chromium');
};

// English languages supported in Safari
export const SAFARI_SUPPORTED_LANGUAGES = {
  'en-US': 'English'
};

interface UseSpeechToTextOptions {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
}

// Supported languages for speech recognition
export const SUPPORTED_LANGUAGES = {
  // English
  'en-US': 'English', // US
  'en-GB': 'English', // UK
  'en-IN': 'English', // India

  // Arabic
  'ar-SA': 'Arabic', // Saudi Arabia
  'ar-EG': 'Arabic', // Egypt
  'ar-AE': 'Arabic', // UAE
  'ar-JO': 'Arabic', // Jordan
  'ar-LB': 'Arabic', // Lebanon
  'ar-MA': 'Arabic', // Morocco
  'ar-TN': 'Arabic', // Tunisia
  'ar-DZ': 'Arabic', // Algeria
  'ar-IQ': 'Arabic', // Iraq
  'ar-KW': 'Arabic', // Kuwait
  'ar-LY': 'Arabic', // Libya
  'ar-QA': 'Arabic', // Qatar
  'ar-SY': 'Arabic', // Syria
  'ar-YE': 'Arabic', // Yemen

  // Indian Languages / भारतीय भाषाएं
  'hi-IN': 'Hindi / हिंदी', // India
  'bn-IN': 'Bengali / বাংলা', // India
  'bn-BD': 'Bengali / বাংলা', // Bangladesh
  'ta-IN': 'Tamil / தமிழ்', // India
  'ta-LK': 'Tamil / தமிழ்', // Sri Lanka
  'te-IN': 'Telugu / తెలుగు', // India
  'mr-IN': 'Marathi / मराठी', // India
  'gu-IN': 'Gujarati / ગુજરાતી', // India
  'kn-IN': 'Kannada / ಕನ್ನಡ', // India
  'ml-IN': 'Malayalam / മലയാളം', // India
  'pa-IN': 'Punjabi / ਪੰਜਾਬੀ', // India
  'or-IN': 'Odia / ଓଡ଼ିଆ', // India
  'as-IN': 'Assamese / অসমীয়া', // India
  'ne-NP': 'Nepali / नेपाली', // Nepal
  'si-LK': 'Sinhala / සිංහල', // Sri Lanka
  'ur-IN': 'Urdu / اردو', // India
  'ur-PK': 'Urdu / اردو', // Pakistan

  // European Languages
  'fr-FR': 'French', // France
  'es-ES': 'Spanish', // Spain
  'de-DE': 'German', // Germany
  'it-IT': 'Italian', // Italy
  'pt-BR': 'Portuguese', // Brazil
  'ru-RU': 'Russian', // Russia
  'nl-NL': 'Dutch', // Netherlands
  'sv-SE': 'Swedish', // Sweden
  'da-DK': 'Danish', // Denmark
  'no-NO': 'Norwegian', // Norway
  'fi-FI': 'Finnish', // Finland
  'pl-PL': 'Polish', // Poland
  'cs-CZ': 'Czech', // Czech Republic
  'sk-SK': 'Slovak', // Slovakia
  'hu-HU': 'Hungarian', // Hungary
  'ro-RO': 'Romanian', // Romania
  'bg-BG': 'Bulgarian', // Bulgaria
  'hr-HR': 'Croatian', // Croatia
  'sr-RS': 'Serbian', // Serbia
  'sl-SI': 'Slovenian', // Slovenia
  'et-EE': 'Estonian', // Estonia
  'lv-LV': 'Latvian', // Latvia
  'lt-LT': 'Lithuanian', // Lithuania
  'el-GR': 'Greek', // Greece

  // East Asian Languages
  'zh-CN': 'Chinese (Mandarin)', // China
  'zh-TW': 'Chinese (Traditional)', // Taiwan
  'zh-HK': 'Chinese (Cantonese)', // Hong Kong
  'ja-JP': 'Japanese', // Japan
  'ko-KR': 'Korean', // South Korea
  'th-TH': 'Thai', // Thailand
  'vi-VN': 'Vietnamese', // Vietnam
  'id-ID': 'Indonesian', // Indonesia
  'ms-MY': 'Malay', // Malaysia
  'tl-PH': 'Filipino', // Philippines

  // Middle Eastern & Other Languages
  'fa-IR': 'Persian / فارسی', // Iran
  'he-IL': 'Hebrew / עברית', // Israel
  'tr-TR': 'Turkish', // Turkey
  'az-AZ': 'Azerbaijani', // Azerbaijan
  'ka-GE': 'Georgian', // Georgia
  'hy-AM': 'Armenian', // Armenia
  'eu-ES': 'Basque', // Spain
  'ca-ES': 'Catalan', // Spain
  'gl-ES': 'Galician', // Spain
  'mt-MT': 'Maltese', // Malta
  'is-IS': 'Icelandic', // Iceland
  'cy-GB': 'Welsh', // UK
  'ga-IE': 'Irish', // Ireland
  'af-ZA': 'Afrikaans', // South Africa
  'sw-KE': 'Swahili', // Kenya
  'am-ET': 'Amharic' // Ethiopia
};

// LocalStorage key for persisting language selection
const LANGUAGE_STORAGE_KEY = 'askVantage_speechLanguage';

// Function to detect browser language and map it to supported speech recognition languages
const getBrowserLanguage = (): string => {
  const browserLang = navigator.language || 'en-US';

  // If exact match exists, use it
  if (SUPPORTED_LANGUAGES[browserLang as keyof typeof SUPPORTED_LANGUAGES]) {
    return browserLang;
  }

  // Try to find a language family match (e.g., 'ar' -> 'ar-SA')
  const langCode = browserLang.split('-')[0];
  const supportedLangs = Object.keys(SUPPORTED_LANGUAGES);
  const familyMatch = supportedLangs.find((lang) => lang.startsWith(langCode + '-'));

  if (familyMatch) {
    return familyMatch;
  }

  // Default fallback
  return 'en-US';
};

// Function to get the initial language from localStorage or browser default
const getInitialLanguage = (defaultLang?: string): string => {
  try {
    const storedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storedLang && SUPPORTED_LANGUAGES[storedLang as keyof typeof SUPPORTED_LANGUAGES]) {
      return storedLang;
    }
  } catch (error) {
    console.error('Failed to read language from localStorage:', error);
  }
  return defaultLang || getBrowserLanguage();
};

interface UseSpeechToTextReturn {
  transcript: string;
  isListening: boolean;
  isSupported: boolean;
  error: string | null;
  currentLanguage: string;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
  setLanguage: (language: string) => void;
}

export const useSpeechToText = (options: UseSpeechToTextOptions = {}): UseSpeechToTextReturn => {
  const { lang, continuous = false, interimResults = true } = options;

  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState(() => getInitialLanguage(lang));
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isManualStopRef = useRef(false);
  const accumulatedTranscriptRef = useRef('');

  // Check if speech recognition is supported
  const isSupported =
    typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  // Create a new recognition instance - Safari needs fresh instances
  const createRecognition = useCallback(() => {
    if (!isSupported) return null;

    const SpeechRecognitionConstructor = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionConstructor();

    // Safari doesn't handle continuous mode well, so we disable it for Safari
    // and manually restart if needed
    const useContinuous = continuous && !isSafari();
    recognition.continuous = useContinuous;
    recognition.interimResults = interimResults;
    recognition.lang = currentLanguage;

    return recognition;
  }, [continuous, interimResults, currentLanguage, isSupported]);

  // Setup event handlers for recognition instance
  const setupRecognitionHandlers = useCallback((recognition: SpeechRecognition) => {
    // Use on* handlers instead of addEventListener for better Safari compatibility
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      // Accumulate final transcripts for Safari (which restarts)
      if (finalTranscript) {
        accumulatedTranscriptRef.current +=
          (accumulatedTranscriptRef.current ? ' ' : '') + finalTranscript;
        setTranscript(accumulatedTranscriptRef.current);
      } else if (interimTranscript) {
        // Show interim results combined with accumulated
        const combined = accumulatedTranscriptRef.current
          ? accumulatedTranscriptRef.current + ' ' + interimTranscript
          : interimTranscript;
        setTranscript(combined);
      }
    };

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onend = () => {
      // Safari fires 'end' event after each phrase, even in continuous mode
      // We need to restart unless manually stopped
      if (!isManualStopRef.current && isSafari()) {
        // Longer delay before restart to allow for pauses in speech
        setTimeout(() => {
          if (!isManualStopRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch (e) {
              // Recognition may already be started or stopped
              setIsListening(false);
            }
          }
        }, 3000);
      } else {
        setIsListening(false);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const errorCode = event.error;

      // Handle different error types
      switch (errorCode) {
        case 'no-speech':
          // Don't show error for no speech - just stop gracefully
          setError('No voice input detected. Please try again.');
          break;
        case 'audio-capture':
          setError('Microphone not found. Please check your microphone.');
          break;
        case 'not-allowed':
          setError('Microphone access denied. Please allow microphone access.');
          break;
        case 'network':
          setError('Network error occurred. Please check your connection.');
          break;
        case 'aborted':
          // User aborted - don't show error
          break;
        default:
          setError('Voice input error. Please try again.');
      }

      // For 'no-speech' error on Safari, don't completely stop
      // Just reset and let user try again
      if (errorCode !== 'no-speech' || !isSafari()) {
        setIsListening(false);
        isManualStopRef.current = true;
      }
    };

    // Safari-specific: track when audio starts/ends
    recognition.onaudiostart = () => {
      // Audio capture started
    };

    recognition.onaudioend = () => {
      // Audio capture ended
    };

    recognition.onspeechstart = () => {
      // Speech detected
    };

    recognition.onspeechend = () => {
      // Speech ended - Safari may fire this before onend
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore cleanup errors
        }
        recognitionRef.current = null;
      }
    };
  }, []);

  const startListening = useCallback(() => {
    if (!isSupported) {
      setError('Speech recognition is not supported in this browser');
      return;
    }

    // Reset state
    setError(null);
    setTranscript('');
    accumulatedTranscriptRef.current = '';
    isManualStopRef.current = false;

    // Abort any existing recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch (e) {
        // Ignore abort errors
      }
    }

    // Create fresh recognition instance (important for Safari)
    const recognition = createRecognition();
    if (!recognition) {
      setError('Failed to initialize speech recognition');
      return;
    }

    recognitionRef.current = recognition;
    setupRecognitionHandlers(recognition);

    try {
      recognition.start();
    } catch (err) {
      // Handle "already started" error
      if (err instanceof Error && err.message.includes('already started')) {
        // Try stopping and restarting
        try {
          recognition.stop();
          setTimeout(() => {
            try {
              recognition.start();
            } catch (e) {
              setError('Failed to start speech recognition');
            }
          }, 500);
        } catch (e) {
          setError('Failed to start speech recognition');
        }
      } else {
        setError('Failed to start speech recognition');
      }
    }
  }, [isSupported, createRecognition, setupRecognitionHandlers]);

  const stopListening = useCallback(() => {
    isManualStopRef.current = true;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore stop errors
      }
    }
    setIsListening(false);
  }, []);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    accumulatedTranscriptRef.current = '';
    setError(null);
  }, []);

  const setLanguage = useCallback((language: string) => {
    if (SUPPORTED_LANGUAGES[language as keyof typeof SUPPORTED_LANGUAGES]) {
      setCurrentLanguage(language);
      setError(null);
      // Persist language selection to localStorage
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      } catch (error) {
        console.error('Failed to save language to localStorage:', error);
      }
    } else {
      setError(`Language '${language}' is not supported`);
    }
  }, []);

  return {
    transcript,
    isListening,
    isSupported,
    error,
    currentLanguage,
    startListening,
    stopListening,
    resetTranscript,
    setLanguage
  };
};
